/**
 * Factories Sagas
 */

import { call, put, takeLatest, debounce, select } from '@ufc/core'
import type { PayloadAction } from '@ufc/core'
import { factoriesActions } from './factories.slice'
import { selectFactoriesFilter } from './factories.selectors'
import { factoriesApi, citiesApi, countriesApi, locationsApi } from '../infrastructure/api/factories-api'
import type {
  FactoriesFilter,
  CreateFactoryRequest,
  UpdateFactoryRequest,
  City,
} from '../domain/types'
import { transformFactoryToListItem, transformListItemToSummary } from '../api/transformers'

// Fetch factories saga
function* fetchFactoriesSaga(action: PayloadAction<FactoriesFilter | undefined>): Generator {
  try {
    const filter = action.payload ?? (yield select(selectFactoriesFilter))
    const result = yield call(factoriesApi.getFactoriesPaginated, filter)
    yield put(factoriesActions.fetchFactoriesSuccess(result))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch factories'
    yield put(factoriesActions.fetchFactoriesFailure(message))
  }
}

// Fetch single factory saga
function* fetchFactorySaga(action: PayloadAction<string>): Generator {
  try {
    const factory = yield call(factoriesApi.getFactory, action.payload)
    if (factory) {
      yield put(factoriesActions.fetchFactorySuccess(factory))
    } else {
      yield put(factoriesActions.fetchFactoryFailure('Factory not found'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch factory'
    yield put(factoriesActions.fetchFactoryFailure(message))
  }
}

// Fetch stats saga
function* fetchStatsSaga(): Generator {
  try {
    const stats = yield call(factoriesApi.getFactoryStats)
    yield put(factoriesActions.fetchStatsSuccess(stats))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch stats'
    yield put(factoriesActions.fetchStatsFailure(message))
  }
}

// Fetch countries saga
function* fetchCountriesSaga(): Generator {
  try {
    const countries = yield call(countriesApi.getAll)
    yield put(factoriesActions.fetchCountriesSuccess(countries))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch countries'
    yield put(factoriesActions.fetchCountriesFailure(message))
  }
}

// Fetch cities saga
function* fetchCitiesSaga(action: PayloadAction<string | undefined>): Generator {
  try {
    const countryCode = action.payload
    const result = countryCode
      ? { data: (yield call(citiesApi.getByCountry, countryCode)) as City[], hasMore: false }
      : (yield call(citiesApi.getAll, 1, 25)) as { data: City[]; hasMore: boolean }
    yield put(factoriesActions.fetchCitiesSuccess(result as { data: City[]; hasMore: boolean }))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch cities'
    yield put(factoriesActions.fetchCitiesFailure(message))
  }
}

// Load more cities saga (appends next page)
function* loadMoreCitiesSaga(): Generator {
  try {
    const state = (yield select()) as { factories: { citiesPage: number } }
    const nextPage = state.factories.citiesPage + 1
    const result = (yield call(citiesApi.getAll, nextPage, 25)) as { data: City[]; hasMore: boolean }
    yield put(factoriesActions.loadMoreCitiesSuccess(result))
  } catch (_error) {
    yield put(factoriesActions.loadMoreCitiesFailure())
  }
}

// Fetch locations saga (from addressbook)
function* fetchLocationsSaga(action: PayloadAction<string | undefined>): Generator {
  try {
    const countryCode = action.payload
    const locations = countryCode
      ? yield call(locationsApi.getByCountry, countryCode)
      : yield call(locationsApi.getAll)
    yield put(factoriesActions.fetchLocationsSuccess(locations))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch locations'
    yield put(factoriesActions.fetchLocationsFailure(message))
  }
}

// Create factory saga
function* createFactorySaga(action: PayloadAction<CreateFactoryRequest>): Generator {
  try {
    const factory = yield call(factoriesApi.createFactory, action.payload)
    const listItem = transformFactoryToListItem(factory)
    yield put(factoriesActions.createFactorySuccess(listItem))
    // Refresh the list
    yield put(factoriesActions.fetchFactories())
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to create factory'
    yield put(factoriesActions.createFactoryFailure(message))
  }
}

// Update factory saga
function* updateFactorySaga(
  action: PayloadAction<{ documentId: string; data: UpdateFactoryRequest }>
): Generator {
  try {
    const { documentId, data } = action.payload
    const factory = yield call(factoriesApi.updateFactory, documentId, data)
    const listItem = transformFactoryToListItem(factory)
    yield put(factoriesActions.updateFactorySuccess(listItem))
    // Refresh list to keep filtered/paginated views in sync after updates
    yield put(factoriesActions.fetchFactories())
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update factory'
    yield put(factoriesActions.updateFactoryFailure(message))
  }
}

// Delete factory saga
function* deleteFactorySaga(action: PayloadAction<string>): Generator {
  try {
    const success = yield call(factoriesApi.deleteFactory, action.payload)
    if (success) {
      yield put(factoriesActions.deleteFactorySuccess(action.payload))
      // Refresh the list
      yield put(factoriesActions.fetchFactories())
    } else {
      yield put(factoriesActions.deleteFactoryFailure('Failed to delete factory'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to delete factory'
    yield put(factoriesActions.deleteFactoryFailure(message))
  }
}

// Fetch factories list for header dropdown (Gate Portal)
function* fetchFactoriesListSaga(): Generator {
  try {
    // Fetch all active factories for the dropdown
    const factories = yield call(factoriesApi.getFactories, { status: 'active' })
    // Transform to summary format for the dropdown
    const factoryList = factories as Awaited<ReturnType<typeof factoriesApi.getFactories>>
    const summaries = factoryList.map(transformListItemToSummary)
    yield put(factoriesActions.fetchFactoriesListSuccess(summaries))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch factories list'
    yield put(factoriesActions.fetchFactoriesListFailure(message))
  }
}

// Filter change sagas - auto fetch
function* onFilterChangeSaga(): Generator {
  yield put(factoriesActions.fetchFactories())
}

// Search cities in factory saga (debounced via watcher)
function* searchCitiesInFactorySaga(action: PayloadAction<string>): Generator {
  try {
    const query = action.payload.trim()
    if (!query) {
      yield put(factoriesActions.clearCitiesSearch())
      return
    }
    const cities = yield call(citiesApi.getBySearch, query)
    yield put(factoriesActions.searchCitiesInFactorySuccess(cities as Parameters<typeof factoriesActions.searchCitiesInFactorySuccess>[0]))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to search cities'
    yield put(factoriesActions.searchCitiesInFactoryFailure(message))
  }
}

// Root saga
export function* factoriesSaga(): Generator {
  yield takeLatest(factoriesActions.fetchFactories.type, fetchFactoriesSaga)
  yield takeLatest(factoriesActions.fetchFactory.type, fetchFactorySaga)
  yield takeLatest(factoriesActions.fetchStats.type, fetchStatsSaga)
  yield takeLatest(factoriesActions.fetchCountries.type, fetchCountriesSaga)
  yield takeLatest(factoriesActions.fetchCities.type, fetchCitiesSaga)
  yield takeLatest(factoriesActions.loadMoreCities.type, loadMoreCitiesSaga)
  yield takeLatest(factoriesActions.fetchLocations.type, fetchLocationsSaga)
  yield takeLatest(factoriesActions.fetchFactoriesList.type, fetchFactoriesListSaga)
  yield takeLatest(factoriesActions.createFactory.type, createFactorySaga)
  yield takeLatest(factoriesActions.updateFactory.type, updateFactorySaga)
  yield takeLatest(factoriesActions.deleteFactory.type, deleteFactorySaga)
  // Auto-fetch on filter changes
  yield takeLatest(factoriesActions.searchFactories.type, onFilterChangeSaga)
  yield takeLatest(factoriesActions.filterByStatus.type, onFilterChangeSaga)
  yield takeLatest(factoriesActions.setPage.type, onFilterChangeSaga)
  yield takeLatest(factoriesActions.setPageSize.type, onFilterChangeSaga)
  // City search with debounce for factory form drawer
  yield debounce(300, factoriesActions.searchCitiesInFactory.type, searchCitiesInFactorySaga)
}
